import { useEffect, useRef } from 'react';
import { AppState, Platform } from 'react-native';

// Coalesce a burst of resume-ish events (e.g. `focus` and `visibilitychange`
// firing back-to-back for the same tab-switch) into a single call.
const DEBOUNCE_MS = 250;

/**
 * Fires `onResume` whenever the app/tab comes back into the foreground —
 * the gap `useFocusEffect` doesn't cover, because it only re-fires on
 * in-app router navigation, not on the OS/browser bringing an already-
 * mounted screen back to the front.
 *
 * Web: listens for `visibilitychange` (tab switched back to), `pageshow`
 * (bfcache restore — the actual gap on iPhone Safari/PWA, which can restore
 * a page from the back-forward cache without firing `visibilitychange` or
 * router focus at all), and `focus` (window regains OS focus). All three
 * share one debounce so they don't trigger duplicate refetches.
 *
 * Native: `AppState` 'change' → fires only on the transition to 'active'.
 *
 * The callback is kept in a ref so the effect subscribes its listeners once
 * per mount regardless of `onResume`'s identity changing across renders.
 */
export function useVisibilityRefetch(onResume: () => void): void {
  const onResumeRef = useRef(onResume);
  useEffect(() => {
    onResumeRef.current = onResume;
  }, [onResume]);

  useEffect(() => {
    let debounceHandle: ReturnType<typeof setTimeout> | null = null;
    const fire = () => {
      if (debounceHandle) clearTimeout(debounceHandle);
      debounceHandle = setTimeout(() => {
        debounceHandle = null;
        onResumeRef.current();
      }, DEBOUNCE_MS);
    };

    if (Platform.OS === 'web') {
      const onVisibilityChange = () => {
        if (document.visibilityState === 'visible') fire();
      };
      document.addEventListener('visibilitychange', onVisibilityChange);
      window.addEventListener('pageshow', fire);
      window.addEventListener('focus', fire);
      return () => {
        document.removeEventListener('visibilitychange', onVisibilityChange);
        window.removeEventListener('pageshow', fire);
        window.removeEventListener('focus', fire);
        if (debounceHandle) clearTimeout(debounceHandle);
      };
    }

    const subscription = AppState.addEventListener('change', (nextState) => {
      if (nextState === 'active') fire();
    });
    return () => {
      subscription.remove();
      if (debounceHandle) clearTimeout(debounceHandle);
    };
  }, []);
}
